import React from 'react';
import { X, Share, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface ShareModalProps {
  postId: string;
  post: any;
  onClose: () => void;
}

const ShareModal: React.FC<ShareModalProps> = ({ postId, post, onClose }) => {
  const copyToClipboard = async () => {
    try {
      const shareUrl = `${window.location.origin}/posts/${postId}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
      onClose();
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const shareToSocial = (platform: string) => {
    const shareUrl = `${window.location.origin}/posts/${postId}`;
    const shareText = post?.content?.substring(0, 100) + (post?.content?.length > 100 ? '...' : '');
    
    let url = '';
    switch (platform) {
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        break;
    }
    
    if (url) {
      window.open(url, '_blank', 'width=600,height=400');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-sm w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Share Post</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          {/* Copy Link */}
          <button
            onClick={copyToClipboard}
            className="w-full flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <Share className="w-5 h-5 text-gray-600" />
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-900">Copy Link</p>
              <p className="text-sm text-gray-500">Share via any app</p>
            </div>
          </button>

          {/* WhatsApp */}
          <button
            onClick={() => shareToSocial('whatsapp')}
            className="w-full flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-900">WhatsApp</p>
              <p className="text-sm text-gray-500">Share to WhatsApp</p>
            </div>
          </button>

          {/* Twitter */}
          <button
            onClick={() => shareToSocial('twitter')}
            className="w-full flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-bold text-sm">𝕏</span>
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-900">Twitter</p>
              <p className="text-sm text-gray-500">Share to Twitter</p>
            </div>
          </button>

          {/* Facebook */}
          <button
            onClick={() => shareToSocial('facebook')}
            className="w-full flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-bold text-sm">f</span>
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-900">Facebook</p>
              <p className="text-sm text-gray-500">Share to Facebook</p>
            </div>
          </button>

          {/* LinkedIn */}
          <button
            onClick={() => shareToSocial('linkedin')}
            className="w-full flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-bold text-sm">in</span>
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-900">LinkedIn</p>
              <p className="text-sm text-gray-500">Share to LinkedIn</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
